import { IComplaint } from 'constants/types';
import { listComplaints } from 'lib/api/complaints';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useComplaintsPagination = ({}: {
  collection: string | string[] | undefined | null;
}) => {
  const limit = 20;
  const [data, setData] = useState<IComplaint[]>([]);
  const [count, setCount] = useState(-1);
  const [isFetching, setIsFetching] = useState(false);

  const [page, setPage] = useState(
    data.length ? Math.ceil(data.length / limit) : 1
  );

  const fetchPage = useCallback(
    async (targetPage: number, replace = false) => {
      setIsFetching(true);
      try {
        const response = await listComplaints({ page: targetPage, limit });
        const results = response.results ?? [];
        setData((current) => (replace ? results : [...current, ...results]));
        setCount(Number(response.count ?? results.length));
      } finally {
        setIsFetching(false);
      }
    },
    [limit]
  );

  const resetPagination = useCallback(() => {
    setPage(1);
    setData([]);
    setCount(-1);
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    const isCached = data.length > 0 && Math.ceil(data.length / limit) === page;

    if (!isCached && !isFetching) {
      fetchPage(page, page === 1);
    }
  }, [limit, page, data.length, isFetching, fetchPage]);

  const isLast = useMemo(() => {
    if (count === -1) {
      return false;
    }

    return data.length >= count;
  }, [count, data.length]);

  return {
    resetPagination,
    page,
    data,
    isLast,
    setPage,
    isFetching,
    count: count < 0 ? 0 : count,
  };
};
